import { createApi } from '@reduxjs/toolkit/query/react';

import { fillMissingCells, transformToTable } from '@/api/farming/helpers';
import { IndividualShort } from '@/api/vaccination/registry/types';
import {
  Cell,
  FarmCellUpdatePayload,
  FarmFilter,
  FarmUpdatePayload,
  MatrixData,
  TransformedTableData,
  VaccIndivFilter,
} from '@/pages/Farming/types';
import { UUID } from '@/shared/api/types';
import {
  CELL_ALL_BY_FARM_ID,
  FARM_CELL,
  FARM_CELL_BY_ID,
  GET_FARM,
  INDIVIDUAL,
  UPDATE_FARM_BY_ID,
} from '@/shared/vaccination/api';

import { baseQueryWithReAuth } from '../baseApi';
import { farmingTags } from './tags';

export const farmingApi = createApi({
  reducerPath: 'farmingApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(farmingTags),
  endpoints: (builder) => ({
    getFarm: builder.query<TransformedTableData, FarmFilter>({
      query: (payload) => ({
        url: GET_FARM,
        method: 'GET',
        params: payload,
      }),
      transformResponse: (response: MatrixData) => {
        const filledData = fillMissingCells(response);

        const transformedIndividualsTable = transformToTable(filledData.individualsTable);

        const { individualsTable, ...rest } = response;

        return {
          ...rest,
          indivWithLocation: filledData?.indivWithLocation,
          transformedIndividualsTable,
        };
      },
      providesTags: [farmingTags.FARM],
    }),
    updateFarm: builder.mutation<void, FarmUpdatePayload>({
      query: (payload) => ({
        url: UPDATE_FARM_BY_ID(payload.farmId),
        method: 'PUT',
        body: payload.filters,
      }),
      invalidatesTags: [farmingTags.FARM],
    }),
    updateCell: builder.mutation<Cell, FarmCellUpdatePayload>({
      query: (payload) => ({
        url: FARM_CELL,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [farmingTags.FARM],
    }),
    clearCell: builder.mutation<void, { cellId: UUID }>({
      query: (payload) => ({
        url: FARM_CELL_BY_ID(payload?.cellId),
        method: 'DELETE',
      }),
      invalidatesTags: [farmingTags.FARM],
    }),
    clearFarm: builder.mutation<void, { farmId: UUID }>({
      query: (payload) => ({
        url: CELL_ALL_BY_FARM_ID(payload?.farmId),
        method: 'DELETE',
      }),
      invalidatesTags: [farmingTags.FARM],
    }),
    getFacilityIndividuals: builder.query<IndividualShort[], VaccIndivFilter>({
      query: (payload) => ({
        url: INDIVIDUAL,
        method: 'GET',
        params: payload,
      }),
      providesTags: [farmingTags.FARM_INDIVIDUALS],
    }),
  }),
});

export const {
  useGetFarmQuery,
  useUpdateFarmMutation,
  useClearCellMutation,
  useUpdateCellMutation,
  useClearFarmMutation,
  useLazyGetFacilityIndividualsQuery,
} = farmingApi;
